import { z } from "zod";
import { e164Schema } from "./phone.js";
import { singleLineTextSchema } from "./text.js";
// La telefonia di un'azienda vista dal gestore della piattaforma (piano M4, Task 5, D3 e D18): la accende, ne sceglie le
// impostazioni e la prova; il fornitore lo imposta da solo il server (sottoaccount, chiave, applicazione della voce,
// spazio dell'audio), e nessuno scrive o rilegge credenziali. Le rotte sono `/api/platform/organizations/:id/telephony`.
//
// Gli elenchi qui sotto ripetono quelli del database (`@gioia365/db/schema/platform`, `telephony.ts`), che l'interfaccia
// non importa: un test del server li confronta voce per voce.
export const TELEPHONY_PROVIDERS = ["twilio", "telnyx"];
export const TELEPHONY_PROVISIONING_STATES = ["pending", "ready", "failed"];
/** I passi del provisioning, in ordine; `none` vuol dire che nessuno è ancora finito. */
export const TELEPHONY_PROVISIONING_STEPS = ["none", "account", "api_key", "application", "storage"];
export const TELEPHONY_RECORDING_STORAGE_STATES = ["not_configured", "configured"];
/** Come Gioia365 ha lasciato il sottoaccount presso il fornitore. */
export const TELEPHONY_ACCOUNT_STATES = ["active", "suspended", "closed"];
export const TELEPHONY_CONNECTION_STATUSES = ["active", "disabled"];
/**
 * La verifica di un cellulare presso il fornitore: chiesta, confermata dal webhook firmato del fornitore, oppure non
 * riuscita. Definita una volta qui, per il numero mostrato dell'azienda (R2) e per il parco dei cellulari (R3).
 */
export const CALLER_VERIFICATION_STATES = ["requested", "verified", "failed"];
/** I numeri che un'azienda di prova può chiamare: i telefoni del team, al massimo dieci (D12). */
export const MAX_ALLOWED_DESTINATIONS = 10;
export const telephonyProviderSchema = z.enum(TELEPHONY_PROVIDERS);
export const callerVerificationStateSchema = z.enum(CALLER_VERIFICATION_STATES);
const provisioningStepSchema = z.enum(TELEPHONY_PROVISIONING_STEPS);
/** Da 1 a 10 numeri in E.164; il server ne tiene uno solo per numero ripetuto. */
export const allowedDestinationsSchema = z.array(e164Schema).min(1).max(MAX_ALLOWED_DESTINATIONS);
/**
 * La connessione telefonica di un'azienda: a che punto è il provisioning e che cosa c'è sul fornitore (identificativi, non
 * segreti), il numero mostrato e la sua verifica, le impostazioni. Nessun campo per i segreti, nemmeno «se ci sono»: li
 * crea e li custodisce il server. `provisioning.step` è l'ultimo passo finito; `failedStep`, soltanto quando il
 * provisioning è fallito, il passo che si è fermato; `stalled`, un giro rimasto a metà.
 */
export const telephonyConnectionViewSchema = z.object({
    provider: telephonyProviderSchema,
    provisioning: z.object({
        state: z.enum(TELEPHONY_PROVISIONING_STATES),
        step: provisioningStepSchema,
        failedStep: provisioningStepSchema.exclude(["none"]).nullable(),
        /** Soltanto il codice dell'errore, mai il messaggio del fornitore. */
        error: z.string().nullable(),
        recordingStorage: z.enum(TELEPHONY_RECORDING_STORAGE_STATES),
        /** Un giro ancora `pending` cinque minuti dopo il suo inizio (interrotto a metà): si può riprendere con «Riprova». */
        stalled: z.boolean(),
    }),
    accountRef: z.string().nullable(),
    applicationRef: z.string().nullable(),
    defaultCallerNumber: z.string().nullable(),
    defaultCallerVerification: callerVerificationStateSchema.nullable(),
    defaultCallerVerificationRequestedAt: z.string().nullable(),
    defaultCallerVerifiedAt: z.string().nullable(),
    recordingEnabled: z.boolean(),
    recordingNotice: z.string().nullable(),
    /** Vuoto: nessuna scadenza automatica dell'audio (questione 166). */
    recordingRetentionDays: z.number().int().nullable(),
    allowedDestinations: z.array(z.string()).nullable(),
    status: z.enum(TELEPHONY_CONNECTION_STATUSES),
    accountState: z.enum(TELEPHONY_ACCOUNT_STATES),
    version: z.number().int(),
    checkedAt: z.string().nullable(),
    /** `ok` oppure il motivo della prova non riuscita, soltanto il codice. */
    checkResult: z.string().nullable(),
    updatedAt: z.string(),
});
/** Ogni rotta della telefonia di un'azienda risponde con la vista; `null` se la telefonia non è mai stata accesa. */
export const telephonyConnectionResponseSchema = z.object({ telephony: telephonyConnectionViewSchema.nullable() });
/** `POST …/telephony/enable`: il fornitore e, per un'azienda `prova-`, i numeri ammessi (D12). */
export const enableTelephonyBodySchema = z.strictObject({
    provider: telephonyProviderSchema,
    allowedDestinations: allowedDestinationsSchema.nullable(),
});
/** `PATCH …/telephony`: le impostazioni, con la versione letta (`409 version_conflict` se nel frattempo è cambiata). */
export const telephonySettingsBodySchema = z.strictObject({
    recordingEnabled: z.boolean(),
    recordingNotice: singleLineTextSchema(300).nullable(),
    recordingRetentionDays: z.number().int().min(1).max(3650).nullable(),
    allowedDestinations: allowedDestinationsSchema.nullable(),
    expectedVersion: z.number().int().min(1),
});
/** `POST …/telephony/caller/verify`: il cellulare da verificare presso il fornitore. */
export const verifyCallerBodySchema = z.strictObject({ e164: e164Schema });
/**
 * Come si verifica: chi tiene il telefono digita il codice mostrato quando il fornitore chiama, oppure inserisce quello
 * che riceve. Il numero risulta verificato soltanto con la conferma firmata del fornitore. Una sola definizione, per il numero
 * mostrato dell'azienda (R2) e per il parco dei cellulari (`caller-numbers.ts`, R3).
 */
export const callerVerificationSchema = z.discriminatedUnion("mode", [
    z.object({ mode: z.literal("type_code_on_phone"), code: z.string() }),
    z.object({ mode: z.literal("enter_received_code") }),
]);
export const verifyCallerResponseSchema = z.object({ verification: callerVerificationSchema });
//# sourceMappingURL=telephony.js.map